import _ from 'lodash';
import Debug from '@/util/debug';
import VueSlider from 'vue-slider-component';
import 'vue-awesome/icons/remove';
import 'vue-awesome/icons/expand';
import 'vue-awesome/icons/compress';
import IntervalSlider from '@/components/interval-slider/IntervalSlider.vue';
import RadiusSlider from '@/components/radius-slider/RadiusSlider.vue';
import GenderSelector from '@/components/gender-selector/GenderSelector.vue';
import LocationTypeSelector from '@/components/location-type-selector/LocationTypeSelector.vue';
import LocationsMap from '@/components/locations-map/LocationsMap.vue';
// import LocationsMap from '@/components/locations-map-leaflet/LocationsMapLeaflet.vue';
import ReachCalculator from '@/components/reach-calculator/ReachCalculator.vue';
import LocationsInput from '@/components/locations-input/LocationsInput.vue';
import LanguageSelector from '@/components/language-selector/LanguageSelector.vue';
import InterestSelector from '@/components/interest-selector/InterestSelector.vue';
import PlacementSelector from '@/components/placement-selector/PlacementSelector.vue';
import CampaignSetup from '@/components/campaign-setup/CampaignSetup.vue';
import CampaignRecommendation from '@/components/campaign-recommendation/CampaignRecommendation.vue';
import Loading from '@/components/loading/Loading.vue';
import * as LocationTypes from '@/models/target-location';
import { filterCountriesByLocations } from '@/util/helper';
import offlineCheck from '@/mixins/offline-check';
import { STEP_TARGET, STEP_OFFER, STEP_PROPOSAL } from '@/services/StepService';
import { MAX_LOCATION_COUNT, UNIT_MI } from '@/util/constants';
import { MODE_MULTI_MONTH } from '@/services/ModeService';
import { can } from '@/util/permissions';
import { clearPrefillData } from '@/util/prefill';
import { GOAL_REACH_FREQUENCY, GOAL_LEAD_GENERATION } from '@/services/GoalTypeService';

const debug = Debug('campaign-target');

export default {
  dependencies: [
    'agesService',
    'radiusesService',
    'interestsService',
    'draftService',
    'countryService',
    'genericConfigService',
    'publicConfigService',
    'locationTypesService',
    'googleMapsService',
    'clusterService',
    'adApiService',
  ],
  props: {
    campaign: Object,
  },
  components: {
    'vue-slider': VueSlider,
    'interval-slider': IntervalSlider,
    'radius-slider': RadiusSlider,
    'gender-selector': GenderSelector,
    'location-type-selector': LocationTypeSelector,
    'locations-map': LocationsMap,
    'reach-calculator': ReachCalculator,
    'locations-input': LocationsInput,
    'language-selector': LanguageSelector,
    'interest-selector': InterestSelector,
    'placement-selector': PlacementSelector,
    'campaign-setup': CampaignSetup,
    'campaign-recommendation': CampaignRecommendation,
    loading: Loading,
  },
  mixins: [offlineCheck],
  data() {
    return {
      originalTarget: _.cloneDeep(this.campaign.target),
      disabledButtons: false,
      loading: true,
      changingTarget: false,
      error: null,
      minimized: false,
      reachLoading: false,
      reachError: true,
      constants: {
        ages: null,
        radiuses: null,
        countries: [],
        interests: null,
        config: null,
        publicConfig: null,
        enabledLocations: [],
        locationTypes: [],
        modes: [],
      },
    };
  },
  computed: {
    availableCountries() {
      return this.constants.countries.map(c => c.value);
    },
    recommendationToolHideNonMatcingGoals() {
      return _.get(this, 'constants.config.recommendation_tool_hide_non_matching_goals', false);
    },
    availableLanguages() {
      return filterCountriesByLocations(this.constants.countries, this.campaign.target.locations)
        .reduce((acc, l) => {
          l.languages.forEach(lang => {
            if (acc.indexOf(lang) === -1) acc.push(lang);
          });
          return acc;
        }, []);
    },
    minBounds() {
      return filterCountriesByLocations(this.constants.countries, this.campaign.target.locations)
        .reduce((acc, c) => {
          if (c.map && c.map.minBounds) {
            Object.keys(c.map.minBounds).forEach(key => {
              if (!Object.prototype.hasOwnProperty.call(acc, key)
                  || c.map.minBounds[key] > acc[key]) {
                acc[key] = c.map.minBounds[key];
              }
            });
          }
          return acc;
        }, {});
    },
    mapLocations() {
      return this.campaign.target.locations.map(l => Object.assign(
        {},
        l,
        { radius: this.campaign.target.radius },
      ));
    },
    availableLocationTypes() {
      if (
        this.campaign.target.locations.find(l => l.type === LocationTypes.LOCATION_TYPE_COUNTRY)
      ) {
        return []; // can't select anything else if country is selected
      }

      if (this.campaign.target.locations.length) {
        return [this.campaign.target.locations[0].type]; // can select only multiples of same type
      }

      return this.constants.enabledLocations
        .filter(
          l => this.isOnline || (!this.isOnline && l !== LocationTypes.LOCATION_TYPE_ADDRESS),
        );
    },
    availableRadiuses() {
      if (this.campaign.target.locations
          .filter(l => l.type === LocationTypes.LOCATION_TYPE_COUNTRY).length) {
        return [0]; // country can't have radius
      }

      const type = this.campaign.target.locations.reduce((acc, l) => l.type, null);

      if (this.campaign.target.locations.length > 1) {
        if (type !== LocationTypes.LOCATION_TYPE_ADDRESS) {
          return [0];
        }
      }

      if (type) {
        if (!this.isOnline && this.constants.radiuses.offline[type]) {
          return this.constants.radiuses.offline[type];
        }
        if (this.constants.radiuses.online[type]) {
          return this.constants.radiuses.online[type];
        }
      }

      return this.constants.radiuses.default;
    },
    availableAges() {
      const ages = this.isOnline ? this.constants.ages.online : this.constants.ages.offline;

      const minAge = _.get(this, 'campaign.goal_type', GOAL_REACH_FREQUENCY) === GOAL_LEAD_GENERATION ? 18 : 0;
      return ages.filter(range => range.to > minAge);
    },
    availableClusters() {
      const forcePackage = this.canChangeTarget ? this.campaign.package : false;
      return this.clusterService.availableClusters(this.campaign, forcePackage);
    },
    canAddLocations() {
      return this.availableLocationTypes.length
        && this.campaign.target.locations.length < MAX_LOCATION_COUNT;
    },
    disableNextStep() {
      return !this.availableClusters
        .filter(c => c.minReach <= this.campaign.potentialReach / c.reachDivider)
        .length;
    },
    colors() {
      const colors = _.get(this.constants, 'publicConfig.agent.design.colors', {});
      return !_.isEmpty(colors) ? colors : {};
    },
    showReachWarnings() {
      // Show min reach warning
      this.$nextTick(() => {
        if (
          this.campaign.target.locations.length
          && !this.reachLoading
          && this.disableNextStep
          && !this.reachError
        ) {
          this.$toast.warn({ message: this.$t('pos.target.not-enough-toast') });
        }
      });

      if (this.reachLoading || this.disableNextStep) {
        return [];
      }

      return [...this.availableClusters.reduce((w, c) => {
        if (c.minReach > this.campaign.potentialReach / c.reachDivider) {
          w.add(c.fullModeName.includes(MODE_MULTI_MONTH) ? MODE_MULTI_MONTH : c.fullModeName);
        }
        return w;
      }, new Set())];
    },
    canChangeTarget() {
      const famCampaign = _.get(this.campaign, 'famCampaign', null);
      return can(famCampaign, ['changeTarget']);
    },
    showCampaignRecommendationTool() {
      return (_.get(this.constants, 'config.recommendation_tool_enabled', 0) === '1');
    },
    marketingGoals() {
      return _.get(this.constants, 'config.marketing_goals', []);
    },
    canPlaceOnInstagram() {
      return _.get(this.constants, 'config.use_instagram_allow_change', false);
    },
    defaultInstagramPlacement() {
      return _.get(this.constants, 'config.use_instagram', false);
    },
  },
  methods: {
    scrollToBottom(attr) {
      const element = document.querySelector(attr);
      if (element) {
        const child = element.querySelector('li');
        if (child) {
          const scrollHeight = element.scrollHeight + child.scrollHeight;
          element.scrollTop = scrollHeight;
        }
      }
    },
    onSave() {
      if (this.canChangeTarget) {
        this.$refs.changeTargetModal.show();
        return;
      }

      // If target is changed - reset selected mode, package
      if (!_.isEqual(this.campaign.target, this.originalTarget)) {
        this.campaign.mode = null;
        this.campaign.package = null;
        delete this.campaign.details.duration;
      }

      this.campaign.currentStep = STEP_TARGET;
      this.save()
        .then(id => {
          this.$router.push({
            name: STEP_OFFER,
            params: { id },
          });
        });
    },
    save() {
      return this.draftService.updateDraft(this.$route.params.id, this.campaign)
        .then(id => {
          clearPrefillData();
          return id;
        });
    },
    onAddLocation(loc) {
      debug.info('add location: %o', loc);
      if (loc.type === 'country') {
        this.disabledButtons = true;
      }
      if (this.campaign.target.locations.findIndex(l => {
        if (l.type !== loc.type) {
          return false;
        }

        // name check is for addresses (fbid = null)
        return l.facebookId === loc.facebookId && l.name === loc.name;
      }) !== -1) {
        return; // can't add duplicates
      }

      if (!this.campaign.target.locations.length) {
        // When adding new location reset params to default
        this.draftService.getNew()
          .then(draft => {
            let radius = draft.target.radius;
            if (loc.type === LocationTypes.LOCATION_TYPE_ADDRESS) {
              radius = draft.target.distance_unit === UNIT_MI ? 5 : 10;
            }
            this.campaign.target.interests = draft.target.interests;
            this.campaign.target.radius = radius;
            this.campaign.target.genders = draft.target.genders;
            this.campaign.target.ages = draft.target.ages;
          })
          .then(() => this.campaign.target.locations.push(loc));
      } else {
        this.campaign.target.locations.push(loc);
      }
    },
    onRemoveLocation(index) {
      debug.info('remove location idx: %d', index);
      this.disabledButtons = false;
      this.campaign.target.locations = [
        ...this.campaign.target.locations.slice(0, index),
        ...this.campaign.target.locations.slice(index + 1),
      ];
      if (this.campaign.target.locations.length === 0) {
        this.campaign.target.radius = 0;
        this.campaign.target.language = null;
      }
    },
    addUserCurrentLocation() {
      if (this.availableLocationTypes
            .findIndex(loc => loc === LocationTypes.LOCATION_TYPE_ADDRESS) >= 0
          && navigator.geolocation
          && this.mapLocations.length === 0
        ) {
        navigator.geolocation.getCurrentPosition(p => {
          this.googleMapsService.initialize().then(() => {
            const geocoder = (window.google && window.google.maps.Geocoder)
            ? new window.google.maps.Geocoder() : null;
            if (geocoder) {
              // Get User current location
              const location = new window.google.maps.LatLng(p.coords.latitude, p.coords.longitude);
              // Find closest place
              geocoder.geocode({ location }, (results, status) => {
                if (status === window.google.maps.GeocoderStatus.OK) {
                  let locatedInCorrectCountry = false;
                  // Check if user is located in current country
                  results.forEach(r => {
                    r.types.forEach(t => {
                      if (t === 'country') {
                        r.address_components.forEach(ac => {
                          if (this.availableCountries.indexOf(ac.short_name) >= 0) {
                            locatedInCorrectCountry = true;
                          }
                        });
                      }
                    });
                  });
                  // Add current location
                  if (locatedInCorrectCountry) {
                    this.onAddLocation(LocationTypes.fromGoogle(results[0]));
                  }
                } else {
                  this.$toast.error({ message: status });
                }
              });
            }
          });
        });
      }
    },
    onHideChangeTargetModal() {
      this.$refs.changeTargetModal.hide();
    },
    onChangeTarget() {
      this.changingTarget = true;
      const fin = () => { this.changingTarget = false; };
      this.save()
        .then(id => this.adApiService.changeTarget(id))
        .then(() => {
          this.$toast.success({ message: this.$t('pos.target.target-updated') });
          this.$refs.changeTargetModal.hide();
          this.$refs.changeTargetSuccessModal.show();
          fin();
        })
        .catch(e => {
          this.$toast.error({ message: e.toString() });
          fin();
        });
    },
    onCloseChangeTargetSuccessModal() {
      this.$router.push({ name: STEP_PROPOSAL, params: { id: this.campaign.id } });
    },
  },
  mounted() {
    const fin = () => {
      debug.info('finished loading. error: %o', this.error);
      this.loading = false;
    };

    Promise.all([
      this.agesService.getAges().then(a => this.constants.ages = a),
      this.radiusesService.getRadiuses().then(r => this.constants.radiuses = r),
      this.countryService.getCountries().then(countries => this.constants.countries = countries),
      this.genericConfigService.getConfig().then(c => {
        this.constants.config = c;
        this.constants.enabledLocations = LocationTypes.enabledLocations(c.enabled_locations);
      }),
      this.publicConfigService.getConfig().then(c => this.constants.publicConfig = c),
      this.locationTypesService.getLocationTypes().then(t => this.constants.locationTypes = t),
      this.interestsService.getInterests().then(i => this.constants.interests = i),
      this.clusterService.getClusters(),
    ])
      .then(fin)
      .catch(e => { this.error = e; fin(); });
  },
  watch: {
    'campaign.target.locations': {
      immediate: true,
      handler: _.debounce(function watchCampaignTargetLocations() {
        this.scrollToBottom('.location-list');
      }, 300),
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/campaign-target/CampaignTarget.js